'use client';

import React, { useEffect, useState } from 'react';
import { Button, Drawer } from '@/components/ui';
import { Input } from '@/components/ui/Input';
import { Field, FieldContent, FieldLabel } from '@/components/ui/Field';
import { academyStudentService } from '@/services/academyStudentService';
import { STUDENTS_MANAGEMENT_TOUR_STEP_IDS } from '@/lib/tour-constants';
import { Loader2 } from 'lucide-react';

const MANUAL_STUDENT_FORM_ID = 'manual-student-form';

export interface AddManualStudentModalProps {
  isOpen: boolean;
  onClose: () => void;
  academyId: string;
  /** Called after the student is created successfully */
  onCreated?: (studentId: string) => void;
  showBackdrop?: boolean;
}

export function AddManualStudentModal({
  isOpen,
  onClose,
  academyId,
  onCreated,
  showBackdrop = true,
}: AddManualStudentModalProps) {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [gender, setGender] = useState<'male' | 'female' | 'other'>('male');
  const [dateOfBirth, setDateOfBirth] = useState('');
  const [skillLevel, setSkillLevel] = useState<'beginner' | 'intermediate' | 'advanced' | ''>('');
  const [inviteEmail, setInviteEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) {
      setFirstName('');
      setLastName('');
      setGender('male');
      setDateOfBirth('');
      setSkillLevel('');
      setInviteEmail('');
      setError(null);
      setSubmitting(false);
    }
  }, [isOpen]);

  const handleClose = () => {
    if (!submitting) {
      onClose();
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const fn = firstName.trim();
    const ln = lastName.trim();
    if (!fn || !ln) {
      setError('First name and last name are required.');
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      const res = await academyStudentService.createManualStudent(academyId, {
        firstName: fn,
        lastName: ln,
        gender,
        ...(dateOfBirth.trim() ? { dateOfBirth: dateOfBirth.trim() } : {}),
        ...(skillLevel ? { skillLevel } : {}),
        ...(inviteEmail.trim() ? { inviteEmail: inviteEmail.trim() } : {}),
      });

      if (res.success && res.data?.id) {
        onCreated?.(res.data.id);
        onClose();
      } else {
        setError(res.error || 'Failed to create student');
      }
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to create student');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Drawer
      isOpen={isOpen}
      onClose={handleClose}
      title="Add student"
      description="Create a roster student manually. Leave invite email blank if you only need internal tracking; add an email when the guardian or adult student should finish signup online."
      width="lg"
      panelId={STUDENTS_MANAGEMENT_TOUR_STEP_IDS.MANUAL_FORM_DRAWER}
      showBackdrop={showBackdrop}
      footer={
        <div className="flex justify-end gap-2">
          <Button type="button" variant="outline" onClick={handleClose} disabled={submitting}>
            Cancel
          </Button>
          <Button
            id={STUDENTS_MANAGEMENT_TOUR_STEP_IDS.MANUAL_FORM_SUBMIT}
            type="submit"
            form={MANUAL_STUDENT_FORM_ID}
            disabled={submitting}
            className="inline-flex items-center gap-2"
          >
            {submitting ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin shrink-0" aria-hidden />
                Saving…
              </>
            ) : (
              'Create student'
            )}
          </Button>
        </div>
      }
    >
      <form
        id={MANUAL_STUDENT_FORM_ID}
        onSubmit={(e) => void handleSubmit(e)}
        className="space-y-4"
      >
        <div id={STUDENTS_MANAGEMENT_TOUR_STEP_IDS.MANUAL_FORM_DETAILS} className="space-y-4">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Field>
            <FieldLabel htmlFor="manual-student-first">First name</FieldLabel>
            <FieldContent>
              <Input
                id="manual-student-first"
                value={firstName}
                onChange={(ev) => setFirstName(ev.target.value)}
                autoComplete="given-name"
                required
              />
            </FieldContent>
          </Field>
          <Field>
            <FieldLabel htmlFor="manual-student-last">Last name</FieldLabel>
            <FieldContent>
              <Input
                id="manual-student-last"
                value={lastName}
                onChange={(ev) => setLastName(ev.target.value)}
                autoComplete="family-name"
                required
              />
            </FieldContent>
          </Field>
        </div>

        <Field>
          <FieldLabel htmlFor="manual-student-gender">Gender</FieldLabel>
          <FieldContent>
            <select
              id="manual-student-gender"
              value={gender}
              onChange={(ev) => setGender(ev.target.value as 'male' | 'female' | 'other')}
              className="w-full rounded-md border border-input bg-background py-2 px-3 text-sm text-gray-900 dark:text-gray-100"
            >
              <option value="male">Male</option>
              <option value="female">Female</option>
              <option value="other">Other</option>
            </select>
          </FieldContent>
        </Field>

        <Field>
          <FieldLabel htmlFor="manual-student-dob">Date of birth (optional)</FieldLabel>
          <FieldContent>
            <Input
              id="manual-student-dob"
              type="date"
              value={dateOfBirth}
              onChange={(ev) => setDateOfBirth(ev.target.value)}
            />
          </FieldContent>
        </Field>

        <Field>
          <FieldLabel htmlFor="manual-student-skill">Skill level (optional)</FieldLabel>
          <FieldContent>
            <select
              id="manual-student-skill"
              value={skillLevel}
              onChange={(ev) =>
                setSkillLevel(ev.target.value as '' | 'beginner' | 'intermediate' | 'advanced')
              }
              className="w-full rounded-md border border-input bg-background py-2 px-3 text-sm text-gray-900 dark:text-gray-100"
            >
              <option value="">Not set</option>
              <option value="beginner">Beginner</option>
              <option value="intermediate">Intermediate</option>
              <option value="advanced">Advanced</option>
            </select>
          </FieldContent>
        </Field>

        <Field>
          <FieldLabel htmlFor="manual-student-invite-email">Invite email (optional)</FieldLabel>
          <FieldContent>
            <Input
              id="manual-student-invite-email"
              type="email"
              value={inviteEmail}
              onChange={(ev) => setInviteEmail(ev.target.value)}
              autoComplete="off"
              placeholder="parent@example.com"
            />
          </FieldContent>
        </Field>

        {error ? (
          <p className="text-sm text-destructive" role="alert">
            {error}
          </p>
        ) : null}
        </div>
      </form>
    </Drawer>
  );
}
